import { reaction } from 'mobx';

import { ICustomFieldViewModel, CustomFieldType, FundCustomFieldOptionViewModel, FundCustomFieldViewModel } from '../../webgiving-generated';
import { FormField } from '../form-field';
import { IOptionProps } from '../../primitives/select';
import { action, computed, observable } from 'mobx';
import uuid from 'uuid/v4';
import { FieldValidationFailure, RuleType, ValidatorFunc, createValidatorFunc, fieldValidationSuccess, createRulesFromMetadata } from '../../validation/validation-rules';
import { getCustomFieldPropertyMetadata } from '../../utils/customFieldsValidation';
import { SplitLineItem } from '../../utils/webgiving-utils';

export const amountSuffix = 'AMOUNT';
export const fundSuffix = 'FUND';

type LineItemsViewModelProps = {
	customFields: ICustomFieldViewModel[];
	lineItems?: SplitLineItem[];
	minPaymentAmount: number;
	maxPaymentAmount: number;
	onUpdateCallback: (items: SplitLineItem[], totalAmount: number) => void;
};

export class LineItemsViewModel {

	@observable
	amount: number;

	minPaymentAmount: number;

	maxPaymentAmount: number;

	fundOptions: IOptionProps[];

	highlightedIndex: number | null = null;

	fundField: FundCustomFieldViewModel;

	@observable
	lineItemsFields: { [key: string]: FormField<number | null> | FormField<string> } = {};

	@computed
	get filteredFundOptions(): IOptionProps[] {
		return this.fundOptions.map((option: IOptionProps) => {
			return {
				value: option.value,
				label: option.label,
				disableTranslation: true,
				disabled: !! this.splitLineItems.find(item => item.FundKey === option.value)
			}
		});
	}

	@computed
	get firstNotDisabledFundKey(): string {
		const fund = this.filteredFundOptions.find(option => option.disabled === false);
		return fund ? fund.value : '';
	}

	@computed
	get amountOfFirstLineItem(): number {
		return this.splitLineItems[0] && this.splitLineItems[0].Amount || 0;
	}

	@computed
	get sortedFields() {
		const sortedFields: { [key: string]: [FormField<number>, FormField<string>] } = Object.keys(this.lineItemsFields).reduce((acc, key) => {
			const [id] = key.split(':');
			if (!acc[id]) {
				acc[id] = [
					this.lineItemsFields[`${id}:${amountSuffix}`], this.lineItemsFields[`${id}:${fundSuffix}`],
				];
			}
			return acc;
		}, {} as any);

		return sortedFields;
	}

	@computed
	get splitLineItems() {
		return Object.values(this.sortedFields).reduce((acc, [numField, fundField]) => {
			return ([...acc, { Amount: numField.value, FundKey: fundField.value }]);
		}, [] as SplitLineItem[]);
	}

	@computed
	get totalAmount() {
		const [total] = Object.values(this.lineItemsFields).reduce((acc, field) => {
			const val = field.value;
			if (typeof val === 'number') {
				acc[0] += val;
			}

			return acc;
		}, [0]);

		const roundedTotal = Number(total.toFixed(2));

		return roundedTotal;
	}

	constructor({
		customFields,
		lineItems = [],
		minPaymentAmount,
		maxPaymentAmount,
		onUpdateCallback,
	}: LineItemsViewModelProps) {
		this.minPaymentAmount = minPaymentAmount;
		this.maxPaymentAmount = maxPaymentAmount;

		const fundField = customFields.find(field => field.Type === CustomFieldType.Fund);
		if (fundField) {
			this.fundOptions = (fundField as FundCustomFieldViewModel).Options.map((option: FundCustomFieldOptionViewModel) => ({
				value: option.Value,
				label: option.Label,
				disableTranslation: true
			}));
			this.fundField = fundField as FundCustomFieldViewModel;
			this.updateLineItemFields(lineItems);
		}

		if (onUpdateCallback) {
			reaction(() => [this.splitLineItems, this.totalAmount], () => {
				onUpdateCallback(this.splitLineItems, this.totalAmount);
			});
		}
	}

	@action
	updateLineItemFields = (addedItems: SplitLineItem[], setHighlightedIndex: boolean = false): void => {
		const fieldMetadata = getCustomFieldPropertyMetadata(this.fundField);
		const fieldValidationRules = createRulesFromMetadata(fieldMetadata);
		const addedFields = addedItems.reduce((acc, { Amount, FundKey }) => {
			const key = uuid();
			const valId = `${key}:${amountSuffix}`;
			const fdId = `${key}:${fundSuffix}`;

			acc[valId] = new FormField<number>(
				Amount,
				this.createAmountValidator(this.minPaymentAmount),
				{
					minPaymentAmount: this.minPaymentAmount,
					maxPaymentAmount: this.maxPaymentAmount,
				},
			);

			acc[fdId] = new FormField<string>(FundKey, fieldValidationRules, fieldMetadata.translationParams);

			return acc;
		}, {} as any);

		this.lineItemsFields = { ...this.lineItemsFields, ...addedFields };

		if (setHighlightedIndex) {
			const lineItemsFieldsCount: number = Object.keys(this.lineItemsFields).length;
			this.highlightedIndex = lineItemsFieldsCount > 0 ? Math.floor(lineItemsFieldsCount / 2) - 1 : null;
		}
	}

	@action
	initFields = (amount: number, fundKey: string): void => {
		this.addLineItem(amount, fundKey);
		this.addLineItem(0, fundKey && this.fundField.DefaultOptionValue ? this.firstNotDisabledFundKey : '');
	}

	@action
	addLineItem = (amount: number = 0, initialFundKey: string = ''): void => {
		const fundKey = initialFundKey;
		const item = {
			FundKey: fundKey,
			Amount: amount,
		};
		const highlightLineItem = true;
		this.updateLineItemFields([item], highlightLineItem);
	}

	@action
	removeLineItem = (parentKey: string): void => {
		const valId = `${parentKey}:${amountSuffix}`;
		const fdId = `${parentKey}:${fundSuffix}`;
		const { [valId]: _, [fdId]: __, ...rest } = this.lineItemsFields;
		this.lineItemsFields = rest;
		if (Object.keys(this.sortedFields).length === 1) {
			setTimeout(() => this.clearSplitLineItems(), 0);
		}
	}

	@action
	clearSplitLineItems = () => {
		this.lineItemsFields = {};
	}

	private createAmountValidator = (minPaymentAmount: number): ValidatorFunc<number> => {
		return createValidatorFunc((amount: number) => {
			if (amount === 0) {
				return new FieldValidationFailure(RuleType.Number, `Please enter an amount`);
			}

			if (isNaN(amount) || amount < minPaymentAmount) {
				return new FieldValidationFailure(
					RuleType.Number,
					'Your amount must be above ${{minPaymentAmount}}',
				);
			}

			return fieldValidationSuccess;
		}, RuleType.Number);
	}
}
